import { useState } from 'react'
import { Typography, Breadcrumb, Card, Form, Select, Empty } from 'antd'
import { EditableTable } from '@/components/EditableTable'
import type { ColumnConfig, ChangeSet } from '@/components/EditableTable/types'
import { usePlanIndicators, useUpdatePlanIndicators, useFilials } from '@/hooks/useRefs'
import type { PlanIndicatorFilters, PlanIndicator } from '@/api/refs'

const { Title } = Typography

export default function PlanIndicatorsPage() {
  const [filters, setFilters] = useState<PlanIndicatorFilters>({
    ot_per: undefined,
    fil_code: undefined,
    otd_prof_type: undefined,
  })

  const { data: indicators, isLoading, refetch } = usePlanIndicators(filters)
  const { data: filials } = useFilials()
  const updateMutation = useUpdatePlanIndicators()

  const filialOptions = filials?.map((f) => ({ value: f.fil_code, label: f.fil_name })) || []

  const columns: ColumnConfig[] = [
    {
      field: 'id_otd',
      title: 'ID отделения',
      type: 'text',
      width: 150,
      editable: false,
    },
    {
      field: 'ot_per',
      title: 'Период',
      type: 'text',
      width: 100,
      editable: false,
    },
    {
      field: 'kf_total_prikaz',
      title: 'Коек по приказу',
      type: 'number',
      width: 140,
      editable: true,
      aggregate: 'sum',
    },
    {
      field: 'kf_total_fact',
      title: 'Коек факт',
      type: 'number',
      width: 120,
      editable: true,
      aggregate: 'sum',
    },
    {
      field: 'kf_reserve',
      title: 'В резерве',
      type: 'number',
      width: 120,
      editable: false,
      formula: 'kf_total_prikaz - kf_total_fact',
      aggregate: 'sum',
    },
    {
      field: 'kf_work_plan',
      title: 'План работы',
      type: 'number',
      width: 120,
      editable: true,
      aggregate: 'sum',
    },
    {
      field: 'kz_avg_plan_gen',
      title: 'Плановый КЗ',
      type: 'number',
      width: 120,
      editable: true,
      aggregate: 'avg',
    },
    {
      field: 'cases_plan_gen',
      title: 'План случаев',
      type: 'number',
      width: 130,
      editable: true,
      aggregate: 'sum',
    },
    {
      field: 'income_plan_gen',
      title: 'План дохода',
      type: 'number',
      width: 140,
      editable: true,
      aggregate: 'sum',
    },
  ]

  const handleSave = async (changes: ChangeSet) => {
    if (changes.updated && indicators) {
      const updates: PlanIndicator[] = []
      for (const [key, data] of Object.entries(changes.updated)) {
        const indicator = indicators.find(i => `${i.id_otd}:${i.ot_per}` === key || i.id_otd === key)
        if (indicator) {
          updates.push({
            id_otd: indicator.id_otd,
            ot_per: indicator.ot_per,
            ...data,
          })
        }
      }
      if (updates.length > 0) {
        await updateMutation.mutateAsync(updates)
      }
    }
    refetch()
  }

  // Генерация периодов (последние 12 месяцев)
  const periodOptions = []
  const now = new Date()
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const value = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}`
    const label = date.toLocaleDateString('ru-RU', { year: 'numeric', month: 'long' })
    periodOptions.push({ value, label })
  }

  return (
    <div>
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item>Справочники</Breadcrumb.Item>
        <Breadcrumb.Item>Плановые показатели</Breadcrumb.Item>
      </Breadcrumb>

      <Title level={2}>Плановые показатели</Title>

      <Card style={{ marginBottom: 16 }}>
        <Form layout="inline">
          <Form.Item label="Период">
            <Select
              style={{ width: 200 }}
              placeholder="Выберите период"
              value={filters.ot_per}
              onChange={(value) => setFilters({ ...filters, ot_per: value })}
              options={periodOptions}
              allowClear
            />
          </Form.Item>

          <Form.Item label="Филиал">
            <Select
              style={{ width: 200 }}
              placeholder="Все филиалы"
              allowClear
              value={filters.fil_code}
              onChange={(value) => setFilters({ ...filters, fil_code: value })}
              options={filialOptions}
            />
          </Form.Item>

          <Form.Item label="Тип профиля">
            <Select
              style={{ width: 150 }}
              placeholder="Все"
              allowClear
              value={filters.otd_prof_type}
              onChange={(value) => setFilters({ ...filters, otd_prof_type: value })}
              options={[
                { value: 'КС', label: 'КС' },
                { value: 'ДС', label: 'ДС' },
              ]}
            />
          </Form.Item>
        </Form>
      </Card>

      {!indicators?.length && !isLoading ? (
        <Empty description="Нет данных. Плановые показатели привязаны к отделениям из id_recviz_v2." />
      ) : (
        <EditableTable
          columns={columns}
          dataSource={indicators || []}
          rowKey="id_otd"
          loading={isLoading}
          onSave={handleSave}
          draftKey="plan-indicators"
        />
      )}
    </div>
  )
}
